import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import ApiHelperFunction from "../../api";
import { SelectedOrder } from "./interface";
import { LineItem } from "../../pages/AdminInvoices/CreateInvoice";

interface APIResponse {
  status: number;
  message: string;
  data?: any;
}

export const getOrderList = createAsyncThunk(
  "admin/get-orders-list",
  async (
    {
      offset = 0,
      limit = 10,
      start_date = "",
      end_date = "",
      status = null,
      search = "",
    }: {
      offset?: number;
      limit?: number;
      search: "";
      start_date?: string;
      end_date?: string;
      status?: any;
    },
    { dispatch },
  ) => {
    const token = localStorage.getItem("user_token");

    const queryParams = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString(),
    });
    if (search) {
      queryParams.append("search", search);
    }
    if (start_date) {
      queryParams.append("start_date", start_date);
    }
    if (end_date) {
      queryParams.append("end_date", end_date);
    }
    if (status !== null) {
      queryParams.append("status", String(status));
    }

    const response = await ApiHelperFunction({
      url: `admin/get-orders-list/?${queryParams}`,
      method: "get",
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });

    return {
      data: response?.data?.data || [],
      total_records: response?.data?.total_records || 0,
    };
  },
);

export const getItin = createAsyncThunk(
  "get-itin-data/",
  async (payload: { order_id?: string }, { dispatch, rejectWithValue }) => {
    const token = localStorage.getItem("user_token");
    const queryParams = payload.order_id ? `?order_id=${payload.order_id}` : "";
    try {
      const response = await ApiHelperFunction({
        url: `get-itin-data/${queryParams}`,
        method: "get",
        dispatch,
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: token ? `${token}` : "",
        },
      });

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const getIncorporation = createAsyncThunk(
  "get-incorporation/",
  async (payload: { order_id?: string }, { dispatch }) => {
    const token = localStorage.getItem("user_token");
    const queryParams = payload.order_id ? `?order_id=${payload.order_id}` : "";

    const response = await ApiHelperFunction({
      url: `get-incorporation/${queryParams}`,
      method: "get",
      data: payload,
      dispatch,
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: token ? `${token}` : "",
      },
    });

    let resData = response?.data;

    return resData;
  },
);
export const getMessageData = createAsyncThunk(
  "admin/get-support-chat",
  async (
    payload: { user_id: number | string; order_id: any },
    { dispatch },
  ) => {
    const token = localStorage.getItem("user_token");

    const { user_id, order_id } = payload;

    const queryParams = `?user_id=${user_id}&order_id=${order_id}`;

    const response = await ApiHelperFunction({
      url: `admin/get-support-chat/${queryParams}`,
      method: "get",
      dispatch,
      headers: {
        Authorization: token || "",
      },
    });

    return response?.data;
  },
);
export const getTaxFilingDataByIdAdminOrder = createAsyncThunk(
  "tax-filing/get-tax-filing-files-with-documents",
  async (payload: any, { dispatch, rejectWithValue }) => {
    const token = localStorage.getItem("user_token");
    try {
      const response = await ApiHelperFunction({
        url: `tax-filing/get-tax-filing-files-with-documents?order_id=${payload?.order_id}`,
        method: "get",
        dispatch,
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `${token}` : "",
        },
      });

      return response?.data?.data;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);
export const handleSendMessage = createAsyncThunk(
  "send-message",
  async (
    payload: {
      user_id: number;
      order_id: any;
      formData: FormData;
    },
    { dispatch },
  ) => {
    const token = localStorage.getItem("user_token");
    const { user_id, order_id, formData } = payload;
    const queryParams = `?user_id=${user_id}&order_id=${order_id}`;

    const response = await ApiHelperFunction({
      url: `send-message/${queryParams}`,
      method: "post",
      data: formData, // ✅ send only FormData here
      dispatch,
      headers: {
        // ❌ DO NOT manually set Content-Type when using FormData
        Authorization: token ? `${token}` : "",
      },
    });

    return response?.data;
  },
);

export const uploadDocs = createAsyncThunk(
  "admin/submit-verified-forms",
  async (
    payload: {
      order_id: number;
      file_type: number;
      company_formation_file?: File;
      files?: File[];
      // Set once the reviewer has accepted the "no official stamp" prompt.
      seal_acknowledged?: boolean;
    },
    { dispatch, rejectWithValue },
  ) => {
    const token = localStorage.getItem("user_token");

    const formData = new FormData();
    formData.append("order_id", String(payload.order_id));
    formData.append("file_type", String(payload.file_type));

    if (payload.files && payload.files.length > 0) {
      payload.files.forEach((file) => {
        formData.append("files", file);
      });
    }
    if (payload.company_formation_file) {
      formData.append("company_formation_file", payload.company_formation_file);
    }
    if (payload.seal_acknowledged) {
      formData.append("seal_acknowledged", "true");
    }

    try {
      const response = await ApiHelperFunction({
        url: `admin/submit-verified-forms/`,
        method: "post",
        data: formData,
        dispatch,
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: token ? `${token}` : "",
        },
      });
      return response?.data;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

// export const uploadDocs = createAsyncThunk(
//   "admin/submit-verified-forms",
//   async (
//     payload: {
//       order_id: number;
//       file_type: number;
//       file: File;

//     },
//     { dispatch, rejectWithValue }
//   ) => {
//     const token = localStorage.getItem("user_token");

//     const formData = new FormData();
//     formData.append("order_id", String(payload.order_id));
//     formData.append("file_type", String(payload.file_type));
//     formData.append("file", payload.file);

//     try {
//       const response = await ApiHelperFunction({
//         url: `admin/submit-verified-forms`,
//         method: "post",
//         data: formData,
//         dispatch,
//         headers: {
//           "Content-Type": "multipart/form-data",
//           Authorization: token ? `${token}` : "",
//         },
//       });

//       return response?.data;
//     } catch (error: any) {
//       return rejectWithValue(error);
//     }
//   }
// );
export const updateStatus = createAsyncThunk(
  "admin/update-form-status",
  async (
    payload: { order_id: string; file_type: number; [key: string]: any },
    { dispatch },
  ) => {
    const token = localStorage.getItem("user_token");
    const queryParams = new URLSearchParams();
    queryParams.append("order_id", payload.order_id);
    queryParams.append("file_type", String(payload.file_type));
    const response = await ApiHelperFunction({
      url: `admin/update-form-status/?${queryParams.toString()}`,
      method: "put",
      data: payload,
      dispatch,
      headers: {
        Authorization: token ? `${token}` : "",
      },
    });
    return response?.data;
  },
);

export const GetOperatingAgreementDetails = createAsyncThunk(
  "admin/get-operating-agreement-details",
  async (payload: { order_id: string }, { dispatch }) => {
    const token = localStorage.getItem("user_token");
    const queryParams = new URLSearchParams();
    queryParams.append("order_id", payload.order_id);
    const response = await ApiHelperFunction({
      url: `get-operating-agreement-details?${queryParams.toString()}`,
      method: "get",
      data: payload,
      dispatch,
      headers: {
        Authorization: token ? `${token}` : "",
      },
    });
    return response?.data;
  },
);

export const AdminCreateInvoice = createAsyncThunk(
  "admin/invoices",
  async (
    payload: {
      line_item: LineItem[];
      clientDetails: any;
      clientId: number;
      subscription_type: string;
      notes?: string;
    },
    { dispatch },
  ) => {
    try {
      const token = localStorage.getItem("user_token");
      const sanitizeLineItems = (items: LineItem[]) => {
        return items.map((item) => ({
          product_code: item.item,
          description: item.description || "",
          unit_cost: Number(item.unit),
          quantity: Number(item.quantity),
        }));
      };
      const cleanedLineItems = sanitizeLineItems(payload.line_item);
      let upatedPaylaod: any;
      if (!payload?.clientId) {
        upatedPaylaod = {
          client_first_name: payload?.clientDetails?.first_name,
          client_last_name: payload?.clientDetails?.last_name,
          client_email: payload?.clientDetails?.email,
          line_items: cleanedLineItems,
          subscription_type: payload?.subscription_type,
          notes: payload?.notes,
        };
      } else {
        upatedPaylaod = {
          client_id: payload?.clientId,
          line_items: cleanedLineItems,
          subscription_type: payload?.subscription_type,
          notes: payload?.notes,
        };
      }

      const response = await ApiHelperFunction({
        url: `/admin/invoices/`,
        method: "post",
        data: upatedPaylaod,
        dispatch,
        headers: {
          Authorization: token ? `${token}` : "",
        },
      });
      return response?.data;
    } catch (error) {
      return error;
    }
  },
);

export const getAdminInvoiceList = createAsyncThunk(
  "admin/invoices",
  async (
    {
      offset = 0,
      limit = 10,

      search = "",
    }: { offset?: number; limit?: number; role_id?: number; search: "" },
    { dispatch },
  ) => {
    const token = localStorage.getItem("user_token");

    const queryParams = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString(),
    });
    if (search) {
      queryParams.append("search", search);
    }

    const response = await ApiHelperFunction({
      url: `admin/invoices/?${queryParams}`,
      method: "get",
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });

    return {
      data: response?.data?.data || [],
      total_records: response?.data?.total_records || 0,
    };
  },
);

export const GetPaymentInvoiceURL = createAsyncThunk(
  "public/admin-invoices/pay",
  async (payload: { token: string }, { dispatch }) => {
    try {
      const token = localStorage.getItem("user_token");
      const response = await ApiHelperFunction({
        url: `public/admin-invoices/pay`,
        method: "post",
        data: payload,
        dispatch,
        headers: {
          Authorization: token ? `${token}` : "",
        },
      });
      return response?.data;
    } catch (error) {
      return error;
    }
  },
);

interface State {
  status: "idle" | "loading" | "succeeded" | "failed";
  orderDetailstatus: "idle" | "loading" | "succeeded" | "failed";
  btnStatus: "idle" | "loading" | "succeeded" | "failed";
  userData: any;
  formattedData: any;
  error: string;
  total_records?: number;
  typeMessage?: any;
  records_limit?: number;
  data: any[];
  userIds: any[];
  selectedOrder?: SelectedOrder | null;
  clickedUserId?: any;
  incorporationDetails?: [];
  fetchMessageDeatil?: any;
  isMessageData?: boolean;
  itinDetails?: [];
  messageDetails?: any[];
  fetchMessgaeData?: any[];
  selectedFile: null;
  senderName?: "";
  messageStatus?: "idle" | "loading" | "succeeded" | "failed";
  updateStatusState?: "idle" | "loading" | "succeeded" | "failed";
  taxOrderData?: any;
}

const initialState: State = {
  status: "idle",
  orderDetailstatus: "idle",
  btnStatus: "idle",
  updateStatusState: "idle",
  userData: null,
  formattedData: null,
  error: "",
  data: [],
  total_records: 0,
  records_limit: 10,
  selectedOrder: null,
  incorporationDetails: [],
  fetchMessageDeatil: [],
  isMessageData: false,
  clickedUserId: null,
  itinDetails: [],
  senderName: "",
  userIds: [],
  typeMessage: "",
  messageDetails: [],
  fetchMessgaeData: [],
  selectedFile: null,
  messageStatus: "idle",
  taxOrderData: null,
};

export const orderSlice = createSlice({
  name: "itin",
  initialState,
  reducers: {
    resetAll: (state) => {
      state.error = "";
    },
    setSelectedOrder: (
      state,
      action: {
        payload: { orderId: number; productId: number; userId?: number };
      },
    ) => {
      state.selectedOrder = action?.payload;
      localStorage.setItem("selectedOrder", JSON.stringify(action.payload));
    },

    loadSelectedOrderFromStorage: (state) => {
      const stored = localStorage.getItem("selectedOrder");
      if (stored) {
        state.selectedOrder = JSON.parse(stored);
      }
    },
    clearSelectedOrder: (state) => {
      state.selectedOrder = null;
      localStorage.removeItem("selectedOrder");
    },
    setClickedUserId: (state, action) => {
      state.clickedUserId = action.payload; // Store the selected user_id
    },
    setMessage: (state, action) => {
      state.typeMessage = action?.payload;
    },
    removeMessageByUUID: (state, action) => {
      const uuid = action.payload;

      state.fetchMessgaeData = state?.fetchMessgaeData?.filter(
        (msg) => msg.uuid !== uuid,
      );
    },

    setNewMessage: (state, action) => {
      const newMessage = action.payload;

      if (Array.isArray(state.fetchMessgaeData)) {
        const data = [...state.fetchMessgaeData, newMessage];
        state.fetchMessgaeData = data;
      } else {
        state.fetchMessgaeData = [newMessage];
      }
    },
    setSelectedFile: (state, action) => {
      state.selectedFile = action.payload;
    },
    setSenderName: (state, action) => {
      state.senderName = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder

      // ✅ Get order List
      .addCase(getOrderList.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getOrderList.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.data = action.payload.data;
        state.userIds = action.payload.data.map((item: any, index: any) => {
          return item.user_id;
        });
        state.total_records = action.payload.total_records;
      })
      .addCase(getOrderList.rejected, (state, action) => {
        state.status = "failed";
        // state.error = action.error.message;
      })
      .addCase(getTaxFilingDataByIdAdminOrder.pending, (state) => {
        state.btnStatus = "loading";
      })
      .addCase(getTaxFilingDataByIdAdminOrder.fulfilled, (state, action) => {
        state.btnStatus = "succeeded";
        state.taxOrderData = action.payload;
        // console.log(state.taxOrderData,"state.taxOrderData")
      })
      .addCase(getTaxFilingDataByIdAdminOrder.rejected, (state, action) => {
        state.btnStatus = "failed";
        // state.error = action.error.message;
      })
      .addCase(getIncorporation.pending, (state) => {
        state.orderDetailstatus = "loading";
        state.error = "";
      })
      .addCase(getIncorporation.fulfilled, (state, action) => {
        state.orderDetailstatus = "succeeded";

        state.fetchMessageDeatil = action.payload?.user_id;

        // state.isMessageData = action?.payload?.no_conversation_yet;
        state.incorporationDetails = action.payload || [];
      })
      .addCase(getIncorporation.rejected, (state, action) => {
        state.orderDetailstatus = "failed";
      })
      .addCase(getItin.pending, (state) => {
        state.orderDetailstatus = "loading";
        state.error = "";
      })
      .addCase(getItin.fulfilled, (state, action) => {
        state.orderDetailstatus = "succeeded";

        state.itinDetails = action.payload || [];
      })
      .addCase(getItin.rejected, (state, action) => {
        state.status = "failed";
      })
      .addCase(getMessageData.pending, (state) => {
        state.messageStatus = "loading";
        state.error = "";
      })
      .addCase(getMessageData.fulfilled, (state, action) => {
        state.messageStatus = "succeeded";

        state.messageDetails = action.payload.data || [];

        state.fetchMessgaeData = action.payload.messages || [];
      })
      .addCase(getMessageData.rejected, (state, action) => {
        state.messageStatus = "failed";
        state.orderDetailstatus = "failed";
      })
      .addCase(updateStatus.pending, (state) => {
        state.updateStatusState = "loading";
      })
      .addCase(updateStatus.fulfilled, (state) => {
        state.updateStatusState = "succeeded";
      })
      .addCase(updateStatus.rejected, (state) => {
        state.updateStatusState = "failed";
      })
      .addCase(handleSendMessage.pending, (state) => {
        state.messageStatus = "loading";
        state.error = "";
      })
      .addCase(handleSendMessage.fulfilled, (state, action) => {
        state.messageStatus = "succeeded";

        const newMessage = action.payload?.data;

        const data: any[] = action.payload.data ? [action.payload.data] : [];
        state.messageDetails = data;
      })
      .addCase(handleSendMessage.rejected, (state, action) => {
        state.messageStatus = "failed";
        state.orderDetailstatus = "failed";
      });
  },
});

export const {
  resetAll,
  setSelectedOrder,
  loadSelectedOrderFromStorage,
  clearSelectedOrder,
  setMessage,
  setSelectedFile,
  setNewMessage,
  removeMessageByUUID,
} = orderSlice.actions;
export default orderSlice.reducer;
